import { Info } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";
import { cn } from "../../lib/utils";
import {
  DASH,
  formatNumber,
  NO_HISTORY_EXPLAINER,
  NO_HISTORY_SCORE,
} from "../../lib/cibil/format";

const MIN = 300;
const MAX = 900;

function band(score: number) {
  if (score >= 800) return { label: "Excellent", tone: "text-green-600" };
  if (score >= 750) return { label: "Very Good", tone: "text-green-600" };
  if (score >= 700) return { label: "Good", tone: "text-blue-600" };
  if (score >= 650) return { label: "Fair", tone: "text-amber-600" };
  return { label: "Needs Attention", tone: "text-red-600" };
}

/** Semi-circular CIBIL score gauge (300–900). */
export function ScoreGauge({
  score,
  bureau = "CIBIL",
  size = "md",
  showStatus = true,
  className,
}: {
  score: number | null;
  bureau?: string;
  size?: "xs" | "sm" | "md";
  /** When false, hides band label (Excellent / Good / etc.). */
  showStatus?: boolean;
  className?: string;
}) {
  const w = size === "xs" ? 92 : size === "sm" ? 132 : 200;
  const h = size === "xs" ? 54 : size === "sm" ? 76 : 112;
  const stroke = size === "xs" ? 7 : size === "sm" ? 9 : 13;
  const r = (w - stroke) / 2;
  const cx = w / 2;
  const cy = h - stroke / 2 - 2;
  const arc = Math.PI * r;

  const noHistory = score === NO_HISTORY_SCORE;
  const pct =
    score === null || noHistory
      ? 0
      : Math.min(1, Math.max(0, (score - MIN) / (MAX - MIN)));
  const meta = score === null || noHistory ? null : band(score);
  const gradId = `score-gauge-grad-${size}`;

  return (
    <div className={cn("flex flex-col items-center", className)}>
      <svg
        width={w}
        height={h}
        viewBox={`0 0 ${w} ${h}`}
        role="img"
        aria-label={`${bureau} score`}
      >
        <defs>
          <linearGradient id={gradId} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#ef4444" />
            <stop offset="55%" stopColor="#f59e0b" />
            <stop offset="100%" stopColor="#22c55e" />
          </linearGradient>
        </defs>
        <path
          d={`M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`}
          fill="none"
          stroke="#e5e7eb"
          strokeWidth={stroke}
          strokeLinecap="round"
        />
        <path
          d={`M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`}
          fill="none"
          stroke={`url(#${gradId})`}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={`${arc * pct} ${arc}`}
          className="transition-[stroke-dasharray] duration-700 ease-out"
        />
      </svg>
      <div
        className={cn(
          "flex flex-col items-center gap-0.5",
          size === "xs" ? "-mt-5" : "-mt-6"
        )}
      >
        <p
          className={cn(
            "font-semibold tabular-nums",
            size === "xs" ? "text-base" : size === "sm" ? "text-xl" : "text-3xl"
          )}
        >
          {score === null ? DASH : noHistory ? "NH" : formatNumber(score)}
          {noHistory ? null : (
            <span
              className={cn(
                "text-gray-400 font-normal",
                size === "xs" ? "text-[10px]" : "text-sm"
              )}
            >
              /{MAX}
            </span>
          )}
        </p>
        {noHistory ? (
          showStatus ? (
            <TooltipProvider delayDuration={100}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span
                    className={cn(
                      "inline-flex cursor-default items-center gap-1 font-medium text-gray-500",
                      size === "xs" ? "text-[10px]" : "text-xs"
                    )}
                  >
                    {bureau} · No credit history
                    <Info className="h-3 w-3" />
                  </span>
                </TooltipTrigger>
                <TooltipContent className="max-w-72 text-xs">
                  <p className="mb-1 font-medium">Where is my CIBIL Score?</p>
                  <p>{NO_HISTORY_EXPLAINER}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : null
        ) : showStatus ? (
          <p
            className={cn(
              size === "xs" ? "text-[10px]" : "text-xs",
              "font-medium",
              meta?.tone ?? "text-gray-500"
            )}
          >
            {meta ? `${bureau} · ${meta.label}` : `${bureau} · Not available`}
          </p>
        ) : null}
      </div>
    </div>
  );
}
